/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Portal } from '@mui/material';
import {
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { lazy } from '../lazy';
import { useLocalStorage } from '../react-components/useLocalStorage';
import AgentEdgeTab from './AgentEdgeTab';
import { AgentPanelContext } from './AgentPanelContext';
import { AgentSessionContext } from './AgentSessionContext';
import { agentSessionStoreReducer } from './components/agentSessionStore';
import { useHorizontalResize } from './components/hooks/useHorizontalResize';
import { getAgentPageContext } from './pageContext';
import type { AgentDraftSeed } from './types';

const AgentSidePanel = lazy(() => import('./AgentSidePanel.js'));
const AgentFullScreen = lazy(() => import('./AgentFullScreen.js'));

const AGENT_PANEL_DESKTOP_MIN_WIDTH = 360;
const AGENT_PANEL_TABLET_MIN_WIDTH = 420;
const AGENT_PANEL_MAX_WIDTH = 900;
const AGENT_PANEL_DEFAULT_WIDTH = 480;
const AGENT_PANEL_MOBILE_BREAKPOINT = 900;
const AGENT_PANEL_WIDTH_STORAGE_KEY = 'agent-side-panel-width';
const AGENT_CONVERSATION_ID_STORAGE_KEY = 'agent-conversation-id';
const AGENT_FULL_SCREEN_STORAGE_KEY = 'agent-full-screen';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getMinimumAgentPanelWidth = () => {
  const viewportWidth = window.innerWidth;
  if (viewportWidth < AGENT_PANEL_MOBILE_BREAKPOINT) return viewportWidth;
  if (viewportWidth < 1200) return AGENT_PANEL_TABLET_MIN_WIDTH;
  return clamp(viewportWidth * 0.2, AGENT_PANEL_DESKTOP_MIN_WIDTH, 520);
};

const getMaximumAgentPanelWidth = () =>
  Math.max(getMinimumAgentPanelWidth(), Math.min(window.innerWidth * 0.72, AGENT_PANEL_MAX_WIDTH));

// Preferred opening width: wider than the floor so the panel is comfortable by
// default, but still clamped within the viewport-derived min/max bounds.
const getDefaultAgentPanelWidth = () =>
  clamp(AGENT_PANEL_DEFAULT_WIDTH, getMinimumAgentPanelWidth(), getMaximumAgentPanelWidth());

const isValidStoredConversationId = (value: string | undefined) =>
  value === undefined || (typeof value === 'string' && value.length > 0);

const isValidStoredFullScreenState = (value: boolean) => {
  if (typeof value !== 'boolean') return false;
  if (!value) return true;

  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;
  return navigation === undefined || navigation.type === 'reload';
};

interface AgentPanelProviderProps {
  // Gates the panel surfaces (side panel, edge tab, full screen). When false the
  // provider only supplies context so callers can stay unconditional.
  enabled?: boolean;
}

export function AgentPanelProvider({
  children,
  enabled = true,
}: Readonly<React.PropsWithChildren<AgentPanelProviderProps>>) {
  const location = useLocation();
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [agentFullScreen, setAgentFullScreen] = useLocalStorage<boolean>(
    AGENT_FULL_SCREEN_STORAGE_KEY,
    false,
    isValidStoredFullScreenState,
  );
  const [agentPanelDraftSeed, setAgentPanelDraftSeed] = useState<AgentDraftSeed>();
  const closeOnNavigationArmed = useRef(!agentFullScreen);
  const locationUrl = `${location.pathname}${location.search}${location.hash}`;
  const previousLocationUrl = useRef(locationUrl);
  // The active conversation survives reloads via localStorage; both the side
  // panel and the full-screen overlay share this single id.
  const [agentConversationId, setAgentConversationId] = useLocalStorage<string | undefined>(
    AGENT_CONVERSATION_ID_STORAGE_KEY,
    undefined,
    isValidStoredConversationId,
  );
  const [agentSessionStore, dispatchAgentSessionAction] = useReducer(agentSessionStoreReducer, {});
  const [agentPanelWidth, setAgentPanelWidth] = useLocalStorage(
    AGENT_PANEL_WIDTH_STORAGE_KEY,
    getDefaultAgentPanelWidth,
    Number.isFinite,
  );

  const constrainedAgentPanelWidth = clamp(
    agentPanelWidth,
    getMinimumAgentPanelWidth(),
    getMaximumAgentPanelWidth(),
  );
  const panelVisible = agentPanelOpen && !agentFullScreen;

  const closeAgentPanel = useCallback(() => {
    setAgentPanelOpen(false);
    setAgentFullScreen(false);
  }, [setAgentFullScreen]);

  const openAgentPanel = useCallback(
    (conversationId?: string) => {
      if (conversationId) setAgentConversationId(conversationId);
      setAgentPanelOpen(true);
    },
    [setAgentConversationId],
  );

  const openNewAgentConversation = useCallback(
    (draftText?: string) => {
      // Dropping the persisted conversation id is what makes this a new thread:
      // otherwise a prefilled quick-start prompt would land at the bottom of
      // whichever conversation the user last had open. Any turn still running in
      // that conversation keeps running, exactly as it does for "New chat".
      setAgentConversationId(undefined);
      setAgentPanelDraftSeed(
        draftText ? (seed) => ({ key: (seed?.key ?? 0) + 1, text: draftText }) : undefined,
      );
      setAgentPanelOpen(true);
    },
    [setAgentConversationId],
  );

  const toggleAgentPanel = useCallback(() => {
    setAgentPanelOpen((open) => !open);
  }, []);

  const enterAgentFullScreen = useCallback(() => setAgentFullScreen(true), [setAgentFullScreen]);

  const exitAgentFullScreen = useCallback(() => {
    setAgentFullScreen(false);
    setAgentPanelOpen(true);
  }, [setAgentFullScreen]);

  useLayoutEffect(() => {
    const routeChanged = previousLocationUrl.current !== locationUrl;
    previousLocationUrl.current = locationUrl;

    if (!agentFullScreen) {
      closeOnNavigationArmed.current = true;
      return;
    }

    if (closeOnNavigationArmed.current) {
      if (routeChanged) closeAgentPanel();
      return;
    }

    // A restored full-screen session mounts before immediate route redirects
    // settle. Arm navigation closing after that initial effect chain so reloads
    // survive canonical redirects, while every later router change closes it.
    const timeout = window.setTimeout(() => {
      closeOnNavigationArmed.current = true;
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [agentFullScreen, closeAgentPanel, locationUrl]);

  const pageContext = useMemo(
    () => getAgentPageContext({ pathname: location.pathname, search: location.search }),
    [location.pathname, location.search],
  );

  const handleAgentPanelPointerMove = useCallback(
    (event: globalThis.PointerEvent) => {
      setAgentPanelWidth(
        clamp(
          window.innerWidth - event.clientX,
          getMinimumAgentPanelWidth(),
          getMaximumAgentPanelWidth(),
        ),
      );
    },
    [setAgentPanelWidth],
  );
  const handleAgentPanelResizeStart = useHorizontalResize(handleAgentPanelPointerMove);

  // The side panel width is a transient, per-session preference: reset it to the
  // default each time the panel opens rather than persisting the last drag.
  useEffect(() => {
    if (agentPanelOpen) {
      setAgentPanelWidth(getDefaultAgentPanelWidth());
    } else {
      // The panel content unmounts on close, so a stale seed would re-prefill
      // the input on the next plain open — drop it.
      setAgentPanelDraftSeed(undefined);
    }
  }, [agentPanelOpen, setAgentPanelWidth]);

  const contextValue = useMemo(
    () => ({
      closeAgentPanel,
      enterAgentFullScreen,
      exitAgentFullScreen,
      isAgentFullScreen: agentFullScreen,
      isAgentPanelOpen: agentPanelOpen,
      openAgentPanel,
      openNewAgentConversation,
      toggleAgentPanel,
    }),
    [
      agentFullScreen,
      agentPanelOpen,
      closeAgentPanel,
      enterAgentFullScreen,
      exitAgentFullScreen,
      openAgentPanel,
      openNewAgentConversation,
      toggleAgentPanel,
    ],
  );
  const sessionContextValue = useMemo(
    () => ({
      dispatchSessionAction: dispatchAgentSessionAction,
      sessionStore: agentSessionStore,
    }),
    [agentSessionStore],
  );

  return (
    <AgentSessionContext.Provider value={sessionContextValue}>
      <AgentPanelContext.Provider value={contextValue}>
        {children}
        {enabled && (
          <Portal>
            {/*
             * The tab and the drawer live in one container whose single transform
             * slides them together, so they can never desync — the tab hangs off
             * the container's left edge and rides it in and out. The container is
             * always mounted (chunks preloaded above) so the first open animates
             * exactly like every subsequent one, with no late-mount pop-in.
             */}
            <Box
              sx={(theme) => ({
                position: 'fixed',
                top: 48,
                right: 0,
                bottom: 0,
                width: {
                  xs: '100vw',
                  md: constrainedAgentPanelWidth,
                },
                minWidth: {
                  md: getMinimumAgentPanelWidth(),
                },
                maxWidth: {
                  md: getMaximumAgentPanelWidth(),
                },
                transform: panelVisible ? 'translateX(0)' : 'translateX(100%)',
                transition: theme.transitions.create('transform', {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                zIndex: theme.zIndex.appBar - 1,
              })}
            >
              <AgentEdgeTab />
              <Box inert={!panelVisible} sx={{ height: '100%', minHeight: 0 }}>
                <Suspense fallback={null}>
                  <AgentSidePanel
                    active={panelVisible}
                    conversationId={agentConversationId}
                    draftSeed={agentPanelDraftSeed}
                    pageContext={pageContext}
                    onConversationChange={setAgentConversationId}
                    onClose={closeAgentPanel}
                    onEnterFullScreen={enterAgentFullScreen}
                    onResizeStart={handleAgentPanelResizeStart}
                  />
                </Suspense>
              </Box>
            </Box>
          </Portal>
        )}
        {enabled && agentFullScreen && (
          <Suspense fallback={null}>
            <Portal>
              <Box
                data-testid="agent-full-screen"
                sx={(theme) => ({
                  position: 'fixed',
                  // A true full screen: cover the app bar too (top: 0, above the
                  // app bar's z-index) so the user cannot navigate away without
                  // exiting or closing first. Notification toasts still render
                  // above this (react-toastify z-index 9999); clicking one
                  // navigates, and the close-on-navigation effect closes the
                  // overlay in response.
                  inset: 0,
                  bgcolor: 'background.default',
                  zIndex: theme.zIndex.modal,
                })}
              >
                <AgentFullScreen
                  conversationId={agentConversationId}
                  pageContext={pageContext}
                  onConversationChange={setAgentConversationId}
                  onClose={closeAgentPanel}
                  onExitFullScreen={exitAgentFullScreen}
                />
              </Box>
            </Portal>
          </Suspense>
        )}
      </AgentPanelContext.Provider>
    </AgentSessionContext.Provider>
  );
}
